import { create } from "zustand";
import {
  analyzeMissedMatch,
  applyBonusStackChoice,
  applyMove,
  applyStreak,
  applySwapChoice,
  BLUE_SHELL_AIMS_ITSELF,
  decideStreakTarget,
  legalStackIndicesForTurn,
  buildMatchLeaderboard,
  copiesPerValue,
  decideBonusStackChoice,
  decideMove,
  decideSwapChoice,
  CardMemory,
  legalStackIndicesForFree,
  makeRng,
  newMatchState,
  randomSeed,
  randomSwapChoice,
  recordRound,
  reseatMatch,
  setupGame,
  turnOrderFromRound,
  updateMemoryFromEvents,
  type AIDifficulty,
  type BonusStackChoice,
  type DeckConfig,
  type EngineEvent,
  type GameState,
  type HouseRules,
  type MatchState,
  type MissedMatchAnalysis,
  type PlayerMove,
  type RNG,
  type SwapChoice,
  isTurnInProgress,
} from "@fiveout/core";
import {
  clearRound,
  isRoundFinished,
  loadLocalConfig,
  loadRound,
  saveLocalConfig,
  SAVE_SCHEMA_VERSION,
  saveRound,
  type ResumableRound,
  type StoredLocalConfig,
} from "./persistence";
import {
  emptyXpBreakdown,
  mergeXpBreakdowns,
  newRoundTally,
  localRoundVenue,
  recordRoundCompleted,
  recordRoundDealt,
  humanSeat,
  tallyEvents,
  tallyMissedMatch,
  tallyStarfall,
  type RoundTally,
  type XpBreakdown,
} from "../stats/recorder";
import type { Achievement } from "../stats/achievements";

export interface LocalPlayerConfig {
  id: string;
  nickname: string;
  isAI: boolean;
  aiDifficulty?: AIDifficulty;
}

export interface LocalGameConfig {
  players: LocalPlayerConfig[];
  deckConfig: DeckConfig;
  houseRules: HouseRules;
  turnTimerSeconds: number | null;
}

/**
 * One move's worth of engine events plus the authoritative state *after* that
 * move. The UI plays batches strictly in order, showing each batch's
 * `stateAfter` (turn ring, HUD chip, board) only once its animation finishes —
 * so fast AI moves can never race the presentation ahead of what's on screen.
 */
export interface EventBatch {
  events: EngineEvent[];
  stateAfter: GameState;
  /**
   * The Match guess this move passed up, if any — computed here because it needs
   * the board from BEFORE the move, which the events don't carry. Drives both the
   * on-table glow and the end-of-round counters. Null for AI seats: an AI's missed
   * Match is not the player's to learn from.
   */
  missedMatch: MissedMatchAnalysis | null;
}

interface GameStoreState {
  config: LocalGameConfig | null;
  gameState: GameState | null;
  match: MatchState;
  /** Per-move animation batches; the Pixi layer consumes these one at a time, in order. */
  pendingBatches: EventBatch[];
  turnCount: number;
  /** Stable id for the current round, so refreshing can't inflate "rounds played". */
  roundId: string | null;
  tally: RoundTally;
  /** Placement on the solo top-10 board for the round just finished, if any. */
  soloPlacement: number | null;
  /** Achievements unlocked by the round just finished, queued for toasts. */
  freshAchievements: Achievement[];
  /** What the round just finished paid out in XP, for the post-round summary. */
  roundXp: XpBreakdown;
  rng: RNG;
  memory: CardMemory | null;

  /**
   * Deals the first round of a NEW match: the leaderboard starts empty.
   *
   * `keepMatch` is the host re-setting the table between rounds and saying no
   * when asked whether to clear the scores — see `changeTable`.
   */
  startGame: (config: LocalGameConfig, opts?: { keepMatch?: boolean }) => void;
  startNextRound: () => void;
  resumeSavedRound: (which?: ResumableRound) => boolean;
  abandonRound: () => void;
  playMove: (playerId: string, move: PlayerMove) => void;
  playBonusChoice: (playerId: string, choice: BonusStackChoice) => void;
  /** One owed Swap: trades the top cards of two piles at the end of `playerId`'s turn. */
  playSwapChoice: (playerId: string, choice: SwapChoice) => void;
  /**
   * Spends the acting player's streak on `targetPlayerId`, or on whoever the
   * rule picks when it is null (Blue Shell). Their own turn still has to be
   * played afterwards either way.
   */
  playStreak: (playerId: string, targetPlayerId: string | null) => void;
  consumeNextBatch: () => void;
  maybePlayAITurn: () => void;
  autoMoveForCurrentPlayer: () => void;
  dismissAchievements: () => void;
}

function randomLegalMoveForDisconnectedOrTimedOutPlayer(state: GameState, rng: RNG): PlayerMove {
  // Per the design doc: on timeout/disconnect, pick a random stack, then a random valid action on it
  // (free placement if the stack is empty/wild-topped, otherwise a random non-boundary guess).
  //
  // The stack comes from the piles the house rules allow rather than all five,
  // so this can never hand applyMove something it will refuse — a timer that
  // produces an illegal move stalls the round it exists to keep moving.
  const allowed = legalStackIndicesForTurn(state);
  const idx = allowed[rng.nextInt(allowed.length)];
  const stack = state.stacks[idx];
  const top = stack.cards[0];
  if (!top || top.kind === "wild") {
    return { type: "free-place", stackIndex: idx };
  }
  const guesses: ("higher" | "lower" | "match")[] = ["match"];
  if (top.value < 13) guesses.push("higher");
  if (top.value > 1) guesses.push("lower");
  return { type: "guess", stackIndex: idx, guess: guesses[rng.nextInt(guesses.length)] };
}

/** Converts the persisted table config into the shape startGame wants. */
export function toGameConfig(stored: StoredLocalConfig): LocalGameConfig {
  return {
    players: stored.seats.map((s) => ({
      id: s.id,
      nickname: s.nickname.trim() || s.id,
      isAI: s.kind === "ai",
      aiDifficulty: s.kind === "ai" ? s.aiDifficulty : undefined,
    })),
    deckConfig: stored.deckConfig,
    houseRules: stored.houseRules,
    turnTimerSeconds: stored.turnTimerSeconds,
  };
}

export const useGameStore = create<GameStoreState>((set, get) => ({
  config: null,
  gameState: null,
  match: newMatchState(`m-${Date.now()}`),
  pendingBatches: [],
  turnCount: 0,
  roundId: null,
  tally: newRoundTally(),
  soloPlacement: null,
  freshAchievements: [],
  roundXp: emptyXpBreakdown(),
  rng: makeRng(randomSeed()),
  memory: null,

  startGame(config, opts) {
    if (!opts?.keepMatch) {
      dealRound(config, newMatchState(`m-${Date.now()}`), 1, set, get);
      return;
    }
    // Carrying the leaderboard across a change of table. Two steps, and the
    // order matters: bank the round that has just been played (`recordRound` is
    // idempotent, so a round already folded in is not counted twice), and only
    // then reseat — last place is not a settled number until the finished round
    // is in, which is the same reason the online deal seeds joiners after
    // banking rather than when they joined.
    const { gameState, match } = get();
    const banked = gameState && gameState.scores ? recordRound(match, gameState) : match;
    const reseated = reseatMatch(banked, config.players.map((p) => p.id));
    dealRound(config, reseated, reseated.roundNumber, set, get, seatingAfter(gameState, config));
  },

  /**
   * Deals the next round of the same match, keeping the accumulated totals. A
   * match never formally ends — players simply stop calling this.
   */
  startNextRound() {
    const { config, gameState, match } = get();
    if (!config) return;
    // Bank the finished round before dealing the next one (recordRound is idempotent).
    const banked = gameState && gameState.scores ? recordRound(match, gameState) : match;
    dealRound(config, banked, banked.roundNumber, set, get, seatingAfter(gameState, config));
  },

  /** Restores an in-progress round saved before a refresh. Returns false if there wasn't one. */
  resumeSavedRound(which) {
    const saved = loadRound(which);
    if (!saved) return false;
    set({
      config: toGameConfig(saved.config),
      gameState: saved.gameState,
      match: saved.match,
      pendingBatches: [], // animations are never resumed; the board snaps to state
      turnCount: saved.turnCount,
      roundId: saved.roundId,
      tally: saved.tally,
      soloPlacement: null,
      freshAchievements: [],
      roundXp: emptyXpBreakdown(),
      rng: makeRng(saved.rngSeed, saved.rngSteps),
      memory: CardMemory.restore(saved.memory),
    });
    get().maybePlayAITurn();
    return true;
  },

  abandonRound() {
    clearRound();
    set({ gameState: null, pendingBatches: [], roundId: null, tally: newRoundTally() });
  },

  playMove(playerId, move) {
    const { gameState, memory } = get();
    if (!gameState) return;
    const { state, events } = applyMove(gameState, playerId, move);
    if (memory) updateMemoryFromEvents(memory, events);
    const finishedATurn = turnFinished(events);
    // Read against `gameState` — the board before the move — while we still have it.
    const mover = gameState.players.find((p) => p.id === playerId);
    const missedMatch = mover && !mover.isAI ? analyzeMissedMatch(gameState, events, playerId) : null;
    set((s) => ({
      gameState: state,
      pendingBatches: [...s.pendingBatches, { events, stateAfter: state, missedMatch }],
      turnCount: finishedATurn ? s.turnCount + 1 : s.turnCount,
      tally: tallyMissedMatch(tallyStarfall(tallyEvents({ ...s.tally }, events, humanSeat(gameState)), events, humanSeat(gameState)), missedMatch),
    }));
    afterMove(state, get, set);
  },

  /**
   * Jourdan's Streaks: marks one opponent, whose next turn opens with piles shut.
   *
   * Queued as its own batch, exactly like a move, so the mark reaches its target
   * before the card that follows it — the alternative is a turn resolving onto a
   * table the player has not been shown yet. It ends nobody's turn, so
   * `turnCount` is left alone: the same player is still to play, and the turn
   * clock pauses for the announcement rather than restarting (`state/turnClock.ts`).
   */
  playStreak(playerId, targetPlayerId) {
    const { gameState } = get();
    if (!gameState) return;
    const { state, events } = applyStreak(gameState, playerId, targetPlayerId);
    set((s) => ({
      gameState: state,
      pendingBatches: [...s.pendingBatches, { events, stateAfter: state, missedMatch: null }],
    }));
    afterMove(state, get, set);
  },

  playBonusChoice(playerId, choice) {
    const { gameState, memory } = get();
    if (!gameState) return;
    const { state, events } = applyBonusStackChoice(gameState, playerId, choice);
    if (memory) updateMemoryFromEvents(memory, events);
    const finishedATurn = turnFinished(events);
    set((s) => ({
      gameState: state,
      // Bonus placements are handed out, not guessed, so there's no Match to miss.
      pendingBatches: [...s.pendingBatches, { events, stateAfter: state, missedMatch: null }],
      turnCount: finishedATurn ? s.turnCount + 1 : s.turnCount,
      tally: tallyStarfall(tallyEvents({ ...s.tally }, events, humanSeat(gameState)), events, humanSeat(gameState)),
    }));
    afterMove(state, get, set);
  },

  /**
   * Mirrors `playBonusChoice`: a swap is handed out, not guessed, so there is no
   * Match to miss. The last owed swap ends the turn, which is why `turnCount`
   * can move here.
   */
  playSwapChoice(playerId, choice) {
    const { gameState, memory } = get();
    if (!gameState) return;
    const { state, events } = applySwapChoice(gameState, playerId, choice);
    if (memory) updateMemoryFromEvents(memory, events);
    const finishedATurn = turnFinished(events);
    set((s) => ({
      gameState: state,
      pendingBatches: [...s.pendingBatches, { events, stateAfter: state, missedMatch: null }],
      turnCount: finishedATurn ? s.turnCount + 1 : s.turnCount,
      tally: tallyStarfall(tallyEvents({ ...s.tally }, events, humanSeat(gameState)), events, humanSeat(gameState)),
    }));
    afterMove(state, get, set);
  },

  consumeNextBatch() {
    set((s) => ({ pendingBatches: s.pendingBatches.slice(1) }));
  },

  maybePlayAITurn() {
    const { gameState } = get();
    if (!gameState) return;
    const pending = gameState.pendingDecision;
    if (gameState.phase === "awaiting-bonus-choice") {
      const player = gameState.players.find((p) => p.id === pending?.playerId);
      if (player?.isAI && pending?.type === "match-off-by-one-bonus") {
        window.setTimeout(() => {
          const s = get().gameState;
          if (!s || s.phase !== "awaiting-bonus-choice" || s.pendingDecision?.type !== "match-off-by-one-bonus") return;
          const choice = decideBonusStackChoice(s, get().rng);
          get().playBonusChoice(player.id, choice);
        }, 700);
      }
      return;
    }
    if (gameState.phase === "awaiting-swap-choice") {
      const player = gameState.players.find((p) => p.id === pending?.playerId);
      if (player?.isAI && pending?.type === "swap-top-cards") {
        window.setTimeout(() => {
          const s = get().gameState;
          if (!s || s.phase !== "awaiting-swap-choice" || s.pendingDecision?.type !== "swap-top-cards") return;
          if (s.pendingDecision.playerId !== player.id) return;
          get().playSwapChoice(player.id, decideSwapChoice(s, get().rng));
        }, 700);
      }
      return;
    }
    if (gameState.phase !== "playing") return;
    const current = gameState.players[gameState.currentPlayerIndex];
    if (!current.isAI) return;
    window.setTimeout(() => {
      const s = get().gameState;
      if (!s || s.phase !== "playing") return;
      const currentNow = s.players[s.currentPlayerIndex];
      if (currentNow.id !== current.id) return;
      // A burning deck is spent first and the turn follows. Returning here is
      // not skipping the turn: applying the streak runs `afterMove`, which calls
      // straight back into this with the same player still to act and their
      // streak now zero, so the move arrives one beat later.
      const streakTarget = decideStreakTarget(s, get().rng);
      if (streakTarget) {
        // Under Blue Shell the engine picks, and the sentinel is how the AI says
        // "spend it" without naming anybody — passing a name there would be a
        // choice the rule does not let this seat make.
        get().playStreak(current.id, streakTarget === BLUE_SHELL_AIMS_ITSELF ? null : streakTarget);
        return;
      }
      const move = decideMove(s, get().rng, get().memory ?? undefined);
      get().playMove(current.id, move);
    }, 900);
  },

  autoMoveForCurrentPlayer() {
    // Used for the turn timer expiring on a human player, or a disconnected player's forced turn.
    const { gameState, rng } = get();
    if (!gameState) return;
    // A human who lets the clock run out on an owed swap gets a random pair,
    // the same answer the server gives a timed-out or disconnected seat. Left
    // unanswered the round would sit parked forever: the swap is the one
    // thing standing between this turn and the next.
    //
    // A CPU seat is never answered here: it plays itself off `maybePlayAITurn`,
    // and a random move pushed in for it would be a turn it never chose.
    const isAI = (playerId: string) => !!gameState.players.find((p) => p.id === playerId)?.isAI;
    if (gameState.phase === "awaiting-swap-choice") {
      const pending = gameState.pendingDecision;
      if (pending?.type !== "swap-top-cards" || isAI(pending.playerId)) return;
      get().playSwapChoice(pending.playerId, randomSwapChoice(gameState, rng));
      return;
    }
    if (gameState.phase !== "playing") return;
    const current = gameState.players[gameState.currentPlayerIndex];
    if (current.isAI) return;
    const move = randomLegalMoveForDisconnectedOrTimedOutPlayer(gameState, rng);
    get().playMove(current.id, move);
  },

  dismissAchievements() {
    set({ freshAchievements: [] });
  },
}));

/**
 * Did this batch finish somebody's turn?
 *
 * Three events can say so, and a turn shows up as any one of them: handed on to
 * the next player, its player going out, or the round ending on it. The
 * round-ending turn has no `turn-ended` — there is nobody left to hand the table
 * to — and a turn that both went out AND handed on carries two of the three,
 * which is why this asks whether any is present rather than counting them.
 */
function turnFinished(events: EngineEvent[]): boolean {
  return events.some((e) => e.type === "turn-ended" || e.type === "player-won" || e.type === "round-over");
}

type Setter = (partial: Partial<GameStoreState> | ((s: GameStoreState) => Partial<GameStoreState>)) => void;

/** Shared deal path for the first round and every subsequent round of a match. */
/**
 * The seat order for the round about to be dealt: the finished round's ranking
 * read backwards, most cards first and first out last (`turnOrderFromRound`).
 *
 * The fallback order for ties is the table as it sat LAST round, so repeated
 * ties do not snap back to the order the seats were added in; seats added since
 * have no result and go first. Null when there is no finished round to read —
 * the first deal of a match — which leaves the configured order alone.
 *
 * Only the round is reordered, never `config`: that is the table the host
 * built, and Change Table must keep showing it as they built it.
 */
function seatingAfter(previous: GameState | null, config: LocalGameConfig): string[] | null {
  if (!previous?.cardsLeft || config.players.length < 2) return null;
  const configured = config.players.map((p) => p.id);
  const lastRound = previous.players.map((p) => p.id).filter((id) => configured.includes(id));
  const added = configured.filter((id) => !lastRound.includes(id));
  return turnOrderFromRound([...added, ...lastRound], previous.cardsLeft, previous.roundWinnerIds ?? (previous.winnerPlayerId ? [previous.winnerPlayerId] : []));
}

function dealRound(
  config: LocalGameConfig,
  match: MatchState,
  roundNumber: number,
  set: Setter,
  get: () => GameStoreState,
  seating: string[] | null = null,
): void {
  const byId = new Map(config.players.map((p) => [p.id, p]));
  const seated = seating ? seating.map((id) => byId.get(id)!) : config.players;
  const seed = randomSeed();
  const rng = makeRng(seed);
  const roundId = `${match.matchId}-r${roundNumber}`;
  const gameState = setupGame({
    id: roundId,
    players: seated.map((p) => ({ id: p.id, nickname: p.nickname, isAI: p.isAI, aiDifficulty: p.aiDifficulty })),
    deckConfig: config.deckConfig,
    houseRules: config.houseRules,
    rng,
    turnTimerSeconds: config.turnTimerSeconds,
    roundNumber,
  });

  // "Rounds played" is counted at the deal and keyed by round id, so refreshing
  // mid-round resumes the same round rather than counting a second game.
  const dealtXp = recordRoundDealt(roundId, gameState.deckConfig, config.players.length);

  set({
    config,
    gameState,
    match: { ...match, roundNumber },
    pendingBatches: [],
    turnCount: 0,
    roundId,
    tally: newRoundTally(),
    soloPlacement: null,
    freshAchievements: [],
    roundXp: dealtXp,
    rng,
    memory: new CardMemory(copiesPerValue(gameState.deckConfig.size)),
  });
  persist(get());
  get().maybePlayAITurn();
}

/** Runs after every applied move: persists, grades a finished round, nudges the AI. */
function afterMove(state: GameState, get: () => GameStoreState, set: Setter): void {
  if (state.phase === "round-over" || state.phase === "game-over") {
    handleRoundEnd(state, get, set);
    // Kept, not cleared: a reload now comes back to this round's results (see
    // loadRound's `finished`). The profile is already written by handleRoundEnd,
    // and the next deal overwrites the save.
    persist(get());
  } else {
    persist(get());
  }
  get().maybePlayAITurn();
}

function persist(store: GameStoreState): void {
  const { gameState, config, roundId, match, rng, memory, turnCount, tally } = store;
  if (!gameState || !config || !roundId || !memory) return;
  if (!isTurnInProgress(gameState.phase) && !isRoundFinished(gameState.phase)) return;
  saveRound({
    schemaVersion: SAVE_SCHEMA_VERSION,
    roundId,
    gameState,
    match,
    config: {
      schemaVersion: SAVE_SCHEMA_VERSION,
      seats: config.players.map((p) => ({
        id: p.id,
        nickname: p.nickname,
        kind: p.isAI ? "ai" : "human",
        aiDifficulty: p.aiDifficulty ?? "medium",
      })),
      deckConfig: gameState.deckConfig,
      houseRules: gameState.houseRules,
      turnTimerSeconds: config.turnTimerSeconds,
    },
    rngSeed: rng.seed,
    rngSteps: rng.steps,
    turnCount,
    tally,
    memory: memory.snapshot(),
    savedAtMs: Date.now(),
  });
}

function handleRoundEnd(state: GameState, get: () => GameStoreState, set: Setter): void {
  const { roundId, turnCount, tally, match, config } = get();
  if (!roundId || !config) return;
  // In local play every seat belongs to the person at the device, so the subject
  // is the human who won if one did, otherwise the first human seat.
  const humans = config.players.filter((p) => !p.isAI);
  const subject = humans.find((p) => p.id === state.winnerPlayerId)?.id ?? humans[0]?.id ?? config.players[0].id;

  const result = recordRoundCompleted({
    roundId,
    state,
    tally,
    subjectPlayerId: subject,
    turnCount,
    matchBefore: match,
    // Which is what makes picking the subject above harmless on a table of
    // several people: "the human who won" is always somebody at this device, so
    // a hot-seat round buys no wins for any of them.
    venue: localRoundVenue(config.players),
  });
  set({
    soloPlacement: result.soloPlacement,
    freshAchievements: result.unlocked,
    roundXp: mergeXpBreakdowns(get().roundXp, result.xp),
  });
}

/** The running match table, for the leaderboard screen. */
export function useMatchLeaderboard() {
  const match = useGameStore((s) => s.match);
  const gameState = useGameStore((s) => s.gameState);
  const nicknames: Record<string, string> = {};
  for (const p of gameState?.players ?? []) nicknames[p.id] = p.nickname;
  return buildMatchLeaderboard(match, nicknames);
}

export { legalStackIndicesForFree, loadLocalConfig, saveLocalConfig };
